/**
 * Owns the "Mosaic GL" experiments render path — the lab-panel-only mode
 * (config.ts's isLabEnabled()) that draws the SAME server-baked CONUS
 * mosaic frames mosaicController.ts already polls/decodes, through a
 * fragment shader (mosaicGlShader.ts) with runtime-toggleable sampling
 * (nearest / bilinear / bicubic Catmull-Rom), floor order (before/after
 * interpolation), and palette (banded / continuous) — mosaicGlCompositor.ts
 * does the actual GL work; this file owns polling, decode, and the
 * MapLibre integration.
 *
 * Deliberately an independent poller rather than sharing MosaicController's:
 * this mirrors the existing sibling-controller precedent (MosaicController
 * and FusionController each already own their full poll/decode/render
 * lifecycle; there is no shared-poller pattern anywhere in this client to
 * extend instead) and keeps this file self-contained. The cost is one
 * harmless extra 5s poll of a small JSON manifest, and only while the lab
 * panel is even reachable — app.ts starts this controller only alongside
 * MosaicController, never on its own. Station-strip/hero-panel data stays
 * sourced from MosaicController's own roster/contributing methods
 * regardless of which layer is actually on screen, since both controllers
 * are always rendering the identical underlying frames.
 *
 * MapLibre integration follows fusionController.ts's pattern, not
 * mosaicController.ts's: the compositor draws into an offscreen canvas
 * covering the CURRENT VIEWPORT (not the mosaic frame's own fixed extent),
 * re-rendered on every moveend/zoomend via a UV transform
 * (fusionGeo.ts's stationUvTransform — a plain Bounds3857->Bounds3857
 * mapper, nothing station-specific despite the name) from the viewport
 * window into the mosaic texture's own bbox, then pushed into a single
 * MapLibre `image` source via updateImage({image: canvas, coordinates}).
 * ONE source/layer, not a two-slot ping-pong crossfade like
 * mosaicController.ts/fusionController.ts use: this is an opt-in research
 * mode being compared frame-by-frame against real storms, so a hard swap
 * on data/LOD changes is simpler, and the occasional momentary pop is an
 * acceptable trade for that simplicity.
 */
import type * as maplibregl from "maplibre-gl";
import { mosaicManifestUrl, mosaicFrameUrl } from "./config";
import { ManifestPoller } from "./manifestPoller";
import type { MosaicHotManifest, MosaicManifestFrame } from "./mosaicTypes";
import { overviewLevelForZoom, lonLatBoundsToMercator, stationUvTransform, type OverviewLevel, type Bounds3857 } from "./fusionGeo";
import { FusionDecoder } from "./fusionDecoder";
import { PRECIP_FLOOR_RAW, NO_FLOOR_RAW } from "./fusionCompositor";
import { mosaicFrameCache, decodeCacheKey, getDecodeResult, setDecodeResult, getSmoothedGrid, setSmoothedGrid } from "./mosaicFrameCache";
import { resolveTargetTs } from "./mosaicTimeline";
import { frameAtOrBefore, findNearestUsable } from "./scrub";
import { MosaicSmoother } from "./mosaicSmoothClient";
import { SIGMA_LEVELS } from "./mosaicGlFieldSmoothing";
import { timeAsync, timeSync } from "./perfStages";

/** Same search-radius rationale as mosaicController.ts's own constant of
 * the same name. */
const NEAREST_LOADED_SEARCH_RADIUS = 8;
import {
  MosaicGlCompositor,
  effectiveLodLevel,
  type SamplingMode,
  SAMPLING_MODES,
  type FloorOrder,
  FLOOR_ORDERS,
  type FloorHardness,
  FLOOR_HARDNESS_MODES,
  type PaletteMode,
  PALETTE_MODES,
} from "./mosaicGlCompositor";

export {
  type SamplingMode,
  SAMPLING_MODES,
  type FloorOrder,
  FLOOR_ORDERS,
  type FloorHardness,
  FLOOR_HARDNESS_MODES,
  type PaletteMode,
  PALETTE_MODES,
  SIGMA_LEVELS,
};

/** The full set of toggles a lab-panel preset button applies at once
 * (app.ts's applyLabPreset). Exported as plain data (not app.ts-local) so
 * the exact preset combinations are independently unit-testable without a
 * DOM/map — same reasoning SAMPLING_MODES etc. above are exported as pure
 * arrays rather than only appearing inline in app.ts's UI-building code. */
export interface LabPreset {
  sampling: SamplingMode;
  despeckle: boolean;
  sigma: number;
  floorOrder: FloorOrder;
  floorHardness: FloorHardness;
  palette: PaletteMode;
}

/** The lab panel's "Preset: crisp" button — the original, pre-field-
 * smoothing behavior (nearest sampling, no despeckle/blur, hard floor,
 * banded palette). Not necessarily every toggle's own individual default
 * (floorOrder stays "after" either way — order only matters when floor-
 * before's masking is meaningfully different from floor-after, which crisp
 * mode doesn't specifically demonstrate). */
export const LAB_PRESET_CRISP: LabPreset = {
  sampling: "nearest",
  despeckle: false,
  sigma: 0,
  floorOrder: "after",
  floorHardness: "hard",
  palette: "banded",
};

/** The lab panel's "Preset: smooth" button — the study's validated "ship
 * spec target" (referred to as pipeline "K" in review), the combination
 * approved as the ship candidate: despeckle -> sigma 1.0 -> bicubic ->
 * feathered floor -> continuous palette. */
export const LAB_PRESET_SMOOTH: LabPreset = {
  sampling: "bicubic",
  despeckle: true,
  sigma: 1,
  floorOrder: "after",
  floorHardness: "feathered",
  palette: "continuous",
};

const SOURCE_ID = "radar-mosaic-gl";
const LAYER_ID = `${SOURCE_ID}-layer`;
const GL_OPACITY = 0.85;
/** Output canvas resolution cap, same value/rationale as
 * fusionController.ts's own MAX_OUTPUT_PX — bounds GPU/memory cost on very
 * large or high-DPI displays; the map's own devicePixelRatio-scaled canvas
 * size is used below that. */
const MAX_OUTPUT_PX = 2048;
/** Same cadence as mosaicController.ts's own poller — aligned with the
 * manifest's own max-age=5 (workers/CONTRACTS.md). */
const MANIFEST_POLL_INTERVAL_MS = 5_000;
/** Same discipline as mosaicController.ts's own preloadAhead — fire-and-
 * forget decode+smooth of the next couple of upcoming frames so scrubbing/
 * play forward lands on a cache hit. */
const PRELOAD_AHEAD = 2;

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";
/** Same non-degenerate placeholder rationale as mosaicController.ts's own
 * PLACEHOLDER_COORDS — a zero-area quad crashes MapLibre's Mercator
 * transform setup on load. */
const PLACEHOLDER_COORDS: [[number, number], [number, number], [number, number], [number, number]] = [
  [-1, 1],
  [1, 1],
  [1, -1],
  [-1, -1],
];

export class MosaicGlController {
  private poller: ManifestPoller<MosaicHotManifest>;
  private decoder = new FusionDecoder();
  private compositor: MosaicGlCompositor | null = null;
  /** Off-main-thread despeckle/Gaussian-blur pre-filter (mosaicGlFieldSmoothing.ts,
   * mosaicSmoothWorker.ts) — a separate worker from FusionDecoder's, since it
   * operates on an already-decoded grid and has nothing to do with COG/TIFF
   * parsing (mosaicSmoothProtocol.ts's doc comment). */
  private smoother = new MosaicSmoother();

  /** True once construction succeeded (a usable WebGL context exists) —
   * false means app.ts must stay on (or fall back to) the CPU mosaic path.
   * Meaningful only once `initialized` is true, same two-flag discipline as
   * fusionController.ts's fusionAvailable/initialized. */
  glAvailable = false;
  initialized = false;

  private manifestValue: MosaicHotManifest | null = null;
  private active = false;
  private liveMode = true;
  private lastScrubMs: number | null = null;

  private sampling: SamplingMode = "bicubic";
  private floorOrder: FloorOrder = "after";
  private floorHardness: FloorHardness = "hard";
  private palette: PaletteMode = "banded";
  private floorEnabled = true;

  /** Field-smoothing pre-filter toggles (mosaicGlFieldSmoothing.ts) — both
   * default off/independent (NOT auto-linked to each other): the lab
   * panel's preset buttons (app.ts), not an implicit toggle-following
   * behavior, are the one-click path to the study's validated combination. */
  private despeckleEnabled = false;
  private sigma = 0;

  private layersReady = false;
  private currentTs: string | null = null;
  private currentLevel: OverviewLevel | null = null;
  private currentBbox: Bounds3857 | null = null;
  /** The last successfully DECODED raw grid (pre-smoothing) — kept around so
   * a despeckle/sigma toggle change can re-run the field-smoothing pipeline
   * and re-upload without a redundant fetch/decode, same purpose as
   * mosaicController.ts's own lastResult field. */
  private currentRaw: Uint8Array | null = null;
  private currentRawWidth: number | null = null;
  private currentRawHeight: number | null = null;
  private requestToken = 0;
  /** Monotonic token for the smoothing pipeline specifically (separate from
   * requestToken, which guards the DECODE) — a rapid sigma/despeckle toggle
   * flip can otherwise let a slower, superseded smooth() resolve after a
   * faster later one and repaint over it. */
  private smoothToken = 0;
  private fallbackNotified = false;

  constructor(
    private map: maplibregl.Map,
    private getBaseUrl: () => string,
    private onDataChanged: () => void,
    /** Called at most once: construction failed, or a live WebGL context
     * was lost mid-session. app.ts uses this to snap the lab panel's render
     * mode back to "mosaic" (the CPU path) and reactivate MosaicController —
     * the "WebGL unavailable/context-lost -> CPU mosaic path" fallback
     * requirement, mirroring fusionController.ts's own construction-time
     * fallback but extended to the mid-session context-loss case too, since
     * unlike fusion this path has no legacy per-frame layer already sitting
     * beneath it ready to take over. */
    private onFallback: () => void,
  ) {
    this.poller = new ManifestPoller<MosaicHotManifest>(
      () => mosaicManifestUrl(this.getBaseUrl()),
      (m) => this.onManifest(m),
      () => {
        // mosaic/conus.json absent or erroring — same "may deploy after
        // you" tolerance as mosaicController.ts's own poller.
      },
      MANIFEST_POLL_INTERVAL_MS,
    );
  }

  /** Call once, after the map's "load" event, same as mosaicController.ts/
   * fusionController.ts's own init(). Tries to stand up one WebGL
   * compositor; on failure leaves `glAvailable` false and adds no layer. */
  init(): void {
    if (this.initialized) return;
    try {
      this.compositor = new MosaicGlCompositor();
      this.glAvailable = true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[mosaic-gl] WebGL unavailable, staying on the CPU mosaic path: ${(err as Error).message}`);
      this.glAvailable = false;
      this.initialized = true;
      return;
    }

    if (this.compositor.cappedForTextureSize) {
      // eslint-disable-next-line no-console
      console.warn(`[mosaic-gl] MAX_TEXTURE_SIZE=${this.compositor.maxTextureSize} < 8192 — capped decode at IFD1`);
    }

    this.map.addSource(SOURCE_ID, { type: "image", url: TRANSPARENT_PIXEL, coordinates: PLACEHOLDER_COORDS });
    this.map.addLayer({
      id: LAYER_ID,
      type: "raster",
      source: SOURCE_ID,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    });
    this.layersReady = true;
    this.initialized = true;

    this.map.on("moveend", () => this.onViewportSettled());
    this.map.on("zoomend", () => this.onViewportSettled());
  }

  start(): void {
    this.poller.start();
  }

  stop(): void {
    this.poller.stop();
  }

  setActive(active: boolean): void {
    if (!this.glAvailable || this.active === active) return;
    this.active = active;
    if (this.layersReady) {
      this.map.setPaintProperty(LAYER_ID, "raster-opacity", active ? GL_OPACITY : 0);
    }
    if (active) this.refresh();
  }

  goLive(): void {
    this.liveMode = true;
    if (this.active) this.refresh();
  }

  showAtOrBefore(ms: number): void {
    this.liveMode = false;
    this.lastScrubMs = ms;
    if (!this.active) return;
    this.refresh();
  }

  /** Instant, CACHE-ONLY repaint for app.ts's scrubber "input" handler
   * WHILE dragging — same purpose/search strategy as mosaicController.ts's
   * own showNearestLoaded, adapted for the GL path's two-tier cache: a
   * candidate frame only counts as usable if its decode is cached AND
   * (whenever despeckle/sigma are active) its SMOOTHED grid at the CURRENT
   * toggle settings is also already cached — painting a frame with the
   * wrong toggles' smoothing applied would be worse than not moving at
   * all. Never triggers a fetch/decode/blur. */
  showNearestLoaded(ms: number): void {
    if (!this.active || !this.compositor) return;
    const compositor = this.compositor;
    const frames = this.manifestValue?.frames;
    if (!frames || frames.length === 0) return;
    const level = this.selectLevel();
    const despeckle = this.despeckleEnabled;
    const sigma = this.sigma;
    const atOrBefore = frameAtOrBefore(frames, ms);
    const startIdx = atOrBefore ? frames.indexOf(atOrBefore) : frames.length - 1;

    // GPU smoothing is effectively free (mosaicGlCompositor.ts's
    // smoothOnGpu), so "usable" only needs the DECODE cached — no separate
    // smoothed-grid cache to consult (that cache never gets entries from
    // the GPU path at all, per applySmoothingAndUpload()'s own doc
    // comment). CPU fallback keeps the original, stricter rule: BOTH the
    // decode AND a smoothed grid at the exact current toggles must already
    // be cached, since CPU smoothing is not free and must never run
    // synchronously in this "instant feedback" path.
    const isUsable = (f: MosaicManifestFrame): boolean => {
      if (!mosaicFrameCache.has(decodeCacheKey(f.ts, level))) return false;
      if (compositor.gpuSmoothingAvailable) return true;
      if (despeckle || sigma > 0) return !!getSmoothedGrid(mosaicFrameCache, f.ts, level, despeckle, sigma);
      return true;
    };
    const nearest = findNearestUsable(frames, startIdx, NEAREST_LOADED_SEARCH_RADIUS, isUsable);
    if (!nearest || (nearest.ts === this.currentTs && level === this.currentLevel)) return;

    const decoded = getDecodeResult(mosaicFrameCache, nearest.ts, level);
    if (!decoded) return; // defensive — the predicate above already confirmed a cache hit

    this.currentTs = nearest.ts;
    this.currentLevel = level;
    this.currentBbox = decoded.bbox;
    this.currentRaw = decoded.raw;
    this.currentRawWidth = decoded.width;
    this.currentRawHeight = decoded.height;

    if (compositor.gpuSmoothingAvailable) {
      compositor.setRawTexture(decoded.raw, decoded.width, decoded.height);
      compositor.smoothOnGpu(despeckle, sigma); // free enough to run synchronously even here
    } else {
      let uploadRaw = decoded.raw;
      if (despeckle || sigma > 0) {
        const smoothed = getSmoothedGrid(mosaicFrameCache, nearest.ts, level, despeckle, sigma);
        if (!smoothed) return; // defensive — isUsable already confirmed this
        uploadRaw = smoothed.raw;
      }
      compositor.setRawTexture(uploadRaw, decoded.width, decoded.height);
    }
    this.onDataChanged();
    this.doRender();
  }

  getSampling(): SamplingMode {
    return this.sampling;
  }

  setSampling(mode: SamplingMode): void {
    if (this.sampling === mode) return;
    this.sampling = mode;
    if (this.active) this.doRender();
  }

  getFloorOrder(): FloorOrder {
    return this.floorOrder;
  }

  setFloorOrder(order: FloorOrder): void {
    if (this.floorOrder === order) return;
    this.floorOrder = order;
    if (this.active) this.doRender();
  }

  getFloorHardness(): FloorHardness {
    return this.floorHardness;
  }

  setFloorHardness(hardness: FloorHardness): void {
    if (this.floorHardness === hardness) return;
    this.floorHardness = hardness;
    if (this.active) this.doRender();
  }

  getDespeckleEnabled(): boolean {
    return this.despeckleEnabled;
  }

  setDespeckleEnabled(enabled: boolean): void {
    if (this.despeckleEnabled === enabled) return;
    this.despeckleEnabled = enabled;
    if (this.active) void this.applySmoothingAndUpload();
  }

  getSigma(): number {
    return this.sigma;
  }

  setSigma(sigma: number): void {
    if (this.sigma === sigma) return;
    this.sigma = sigma;
    if (this.active) void this.applySmoothingAndUpload();
  }

  getPalette(): PaletteMode {
    return this.palette;
  }

  setPalette(mode: PaletteMode): void {
    if (this.palette === mode) return;
    this.palette = mode;
    if (this.active) this.doRender();
  }

  getFloorEnabled(): boolean {
    return this.floorEnabled;
  }

  setFloorEnabled(enabled: boolean): void {
    if (this.floorEnabled === enabled) return;
    this.floorEnabled = enabled;
    if (this.active) this.doRender();
  }

  /** Lab panel status line: which frame ts + which IFD/LOD is textured, plus
   * the active field-smoothing settings, so a visual comparison against the
   * CPU mosaic path (and between smoothing settings) is apples-to-apples. */
  statusLine(): string {
    if (!this.glAvailable) return "WebGL unavailable — showing CPU mosaic";
    if (!this.currentTs || this.currentLevel === null) return "no frame decoded yet";
    const smoothing = this.sigma > 0 ? `sigma=${this.sigma}` : "sigma=off";
    const despeckle = this.despeckleEnabled ? "despeckle=on" : "despeckle=off";
    const engine = this.compositor?.gpuSmoothingAvailable ? "gpu" : "cpu-fallback";
    return `ts=${this.currentTs} · IFD${this.currentLevel} · ${despeckle} · ${smoothing} · smooth-engine=${engine}`;
  }

  private onManifest(manifest: MosaicHotManifest): void {
    this.manifestValue = manifest;
    this.onDataChanged();
    if (this.active) this.refresh();
  }

  private onViewportSettled(): void {
    if (!this.active) return;
    this.refresh();
    // A pure pan/zoom with no ts or level change still needs a fresh draw —
    // this canvas only ever covers the CURRENT viewport (unlike
    // mosaicController.ts's static-per-frame image source), same reasoning
    // as fusionController.ts's own onViewportSettled ("the window itself
    // moved even if no station's data changed").
    this.doRender();
  }

  /** Single funnel for "what should be textured right now, at the current
   * zoom's LOD" — mirrors mosaicController.ts's own refresh(), except a
   * level/ts match still triggers a redraw (doRender) rather than a no-op,
   * since the viewport itself (not just the data) drives every render here. */
  private refresh(): void {
    if (this.checkContextLost()) return;
    const targetTs = timeSync("manifest-resolve", () => resolveTargetTs(this.manifestValue, this.liveMode, this.lastScrubMs));
    if (!targetTs) return;
    const level = this.selectLevel();
    if (targetTs === this.currentTs && level === this.currentLevel) {
      this.doRender();
      return;
    }
    void this.swapTo(targetTs, level);
  }

  private selectLevel(): OverviewLevel {
    const zoomLevel = overviewLevelForZoom(this.map.getZoom());
    const maxTextureSize = this.compositor?.maxTextureSize ?? 0;
    return effectiveLodLevel(zoomLevel, maxTextureSize);
  }

  private async swapTo(ts: string, level: OverviewLevel): Promise<void> {
    if (!this.layersReady) return;
    const token = ++this.requestToken;
    let result = getDecodeResult(mosaicFrameCache, ts, level);
    if (!result) {
      try {
        result = await timeAsync(
          "fetch",
          () => this.decoder.decode("MOSAIC", mosaicFrameUrl(this.getBaseUrl(), ts), level, decodeCacheKey(ts, level)),
          (r) => `IFD${level},${(r.bytesTransferred / 1024).toFixed(0)}KiB`,
        );
        // Cache regardless of whether this request is later found stale —
        // see applySmoothingAndUpload()'s matching comment.
        setDecodeResult(mosaicFrameCache, ts, level, result);
      } catch {
        return; // best-effort — leave whatever's currently painted
      }
    }
    if (token !== this.requestToken) return; // superseded — a fresher swapTo already won

    this.currentTs = ts;
    this.currentLevel = level;
    this.currentBbox = result.bbox;
    this.currentRaw = result.raw;
    this.currentRawWidth = result.width;
    this.currentRawHeight = result.height;
    this.onDataChanged();
    void this.applySmoothingAndUpload();
    this.preloadAhead(level);
  }

  /** Applies field smoothing to `currentRaw` whenever it — or the
   * despeckle/sigma toggles — change, then uploads whichever grid results
   * and redraws. Two entirely different mechanisms depending on
   * `compositor.gpuSmoothingAvailable` (mosaicGlCapabilities.ts's
   * empirically-probed decision, fixed for this compositor's whole
   * lifetime — never mixed within one controller instance):
   *
   *  - GPU (primary): mosaicGlCompositor.ts's despeckle+blur FBO passes,
   *    run synchronously right here — no worker round trip, no promise, no
   *    smoothed-grid cache at all (mosaicFrameCache.ts's decoded-grid
   *    entries keep the WHOLE byte budget, since there's nothing left to
   *    compete with them for it). smoothOnGpu() itself no-ops when
   *    despeckle=false && sigma<=0, so render() naturally falls back to
   *    sampling the raw texture directly in that case.
   *  - CPU (fallback, GPU smoothing unavailable): unchanged from before
   *    the GPU path existed — mosaicSmoothClient.ts's worker round trip,
   *    checked against mosaicFrameCache.ts's smoothed-grid cache first so
   *    a revisit at the same (ts, level, despeckle, sigma) is free.
   *
   * mosaicGlShader.ts's DISPLAY kernel doesn't need to know which path
   * produced its texture either way: a smoothed grid has no cell at
   * raw===0 by construction (no-echo-as-low), so the existing nodata-aware
   * mask/coverage logic just degenerates to "always fully valid" — see
   * mosaicGlShader.ts's own doc comment on why that reuse is safe rather
   * than a second, parallel code path. */
  private async applySmoothingAndUpload(): Promise<void> {
    if (!this.currentRaw || this.currentRawWidth === null || this.currentRawHeight === null || !this.currentTs || this.currentLevel === null) return;
    const compositor = this.compositor;
    if (!compositor) return;
    const ts = this.currentTs;
    const level = this.currentLevel;
    const raw = this.currentRaw;
    const width = this.currentRawWidth;
    const height = this.currentRawHeight;
    const despeckle = this.despeckleEnabled;
    const sigma = this.sigma;

    if (compositor.gpuSmoothingAvailable) {
      compositor.setRawTexture(raw, width, height);
      compositor.smoothOnGpu(despeckle, sigma);
      this.doRender();
      return;
    }

    const token = ++this.smoothToken;
    let uploadRaw = raw;
    if (despeckle || sigma > 0) {
      const cached = getSmoothedGrid(mosaicFrameCache, ts, level, despeckle, sigma);
      if (cached) {
        uploadRaw = cached.raw;
      } else {
        try {
          uploadRaw = await timeAsync(
            "field-smooth",
            () => this.smoother.smooth(raw, width, height, despeckle, sigma),
            () => `despeckle=${despeckle},sigma=${sigma}`,
          );
          // Cache even if this request turns out to be stale by the time it
          // resolves (checked below) — the computation happened and the
          // result is still correct for this exact key; a later re-visit to
          // this same (ts, level, despeckle, sigma) should still be free.
          setSmoothedGrid(mosaicFrameCache, ts, level, despeckle, sigma, uploadRaw, width, height);
        } catch {
          uploadRaw = raw; // best-effort — fall back to the unsmoothed grid
        }
      }
    }
    if (token !== this.smoothToken) return; // superseded by a newer toggle flip or frame

    compositor.setRawTexture(uploadRaw, width, height);
    this.doRender();
  }

  /** Fire-and-forget decode+smooth of the next `PRELOAD_AHEAD` upcoming
   * frames at the SAME level and CURRENT toggle settings, same discipline
   * as mosaicController.ts's own preloadAhead — so scrubbing/play forward
   * from `ts` lands on a cache hit for both the decode and (if smoothing is
   * on) the blur. Cancelled implicitly by nothing — best-effort, results
   * just populate the shared cache for whenever they're actually needed. */
  private preloadAhead(level: OverviewLevel): void {
    const frames = this.manifestValue?.frames;
    if (!frames || !this.currentTs) return;
    const startIdx = frames.findIndex((f) => f.ts === this.currentTs) + 1;
    if (startIdx <= 0) return;
    const despeckle = this.despeckleEnabled;
    const sigma = this.sigma;
    for (let k = 0; k < PRELOAD_AHEAD; k++) {
      const f = frames[startIdx + k];
      if (!f) break;
      void this.preloadFrame(f.ts, level, despeckle, sigma);
    }
  }

  private async preloadFrame(ts: string, level: OverviewLevel, despeckle: boolean, sigma: number): Promise<void> {
    let result = getDecodeResult(mosaicFrameCache, ts, level);
    if (!result) {
      try {
        result = await this.decoder.decode("MOSAIC", mosaicFrameUrl(this.getBaseUrl(), ts), level, decodeCacheKey(ts, level));
        setDecodeResult(mosaicFrameCache, ts, level, result);
      } catch {
        return;
      }
    }
    // GPU smoothing recomputes fresh every time (mosaicGlCompositor.ts's
    // smoothOnGpu) — there is no smoothed-grid cache for it to warm, so
    // the decode cache entry above is the entire win on that path. Only
    // the CPU fallback benefits from pre-warming a smoothed grid too.
    if (this.compositor?.gpuSmoothingAvailable) return;
    if (!despeckle && sigma <= 0) return; // nothing more to warm — the decode cache entry alone is the win
    if (getSmoothedGrid(mosaicFrameCache, ts, level, despeckle, sigma)) return;
    try {
      const smoothed = await this.smoother.smooth(result.raw, result.width, result.height, despeckle, sigma);
      setSmoothedGrid(mosaicFrameCache, ts, level, despeckle, sigma, smoothed, result.width, result.height);
    } catch {
      /* best-effort */
    }
  }

  private outputSize(): { width: number; height: number } {
    const canvas = this.map.getCanvas();
    return { width: Math.min(canvas.width, MAX_OUTPUT_PX), height: Math.min(canvas.height, MAX_OUTPUT_PX) };
  }

  private doRender(): void {
    if (this.checkContextLost()) return;
    if (!this.compositor || !this.currentBbox || !this.active) return;
    const bounds = this.map.getBounds();
    const window = lonLatBoundsToMercator({ west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() });
    const uv = stationUvTransform(window, this.currentBbox);
    if (!uv) return; // current viewport doesn't overlap the mosaic's own coverage at all

    const { width, height } = this.outputSize();
    const floorRaw = this.floorEnabled ? PRECIP_FLOOR_RAW : NO_FLOOR_RAW;
    const compositor = this.compositor;
    timeSync("upload-draw", () => {
      compositor.render(uv, width, height, this.sampling, this.floorOrder, floorRaw, this.floorHardness, this.palette);
      const source = this.map.getSource(SOURCE_ID) as maplibregl.ImageSource | undefined;
      const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
        [bounds.getWest(), bounds.getNorth()],
        [bounds.getEast(), bounds.getNorth()],
        [bounds.getEast(), bounds.getSouth()],
        [bounds.getWest(), bounds.getSouth()],
      ];
      source?.updateImage({ image: compositor.canvas, coordinates });
    });
  }

  /** Checks (and, on the first detection, acts on) a lost WebGL context —
   * called at the top of every refresh()/doRender() so a mid-session loss
   * stops silently drawing nothing and instead falls back exactly once. */
  private checkContextLost(): boolean {
    if (!this.compositor?.isLost()) return false;
    if (!this.fallbackNotified) {
      this.fallbackNotified = true;
      this.glAvailable = false;
      this.active = false;
      if (this.layersReady) this.map.setPaintProperty(LAYER_ID, "raster-opacity", 0);
      this.onFallback();
    }
    return true;
  }
}
